import {Component, OnInit} from '@angular/core';
import {AdminService} from "../../services/admin.service";
import {ActivatedRoute} from "@angular/router";
import Swal from "sweetalert2";
import {ImageCroppedEvent} from "ngx-image-cropper";
import 'hammerjs';

@Component({
  selector: 'app-media-upload',
  templateUrl: './media-upload.component.html',
  styleUrls: ['./media-upload.component.scss']
})
export class MediaUploadComponent implements OnInit {
  newsId: string = '';
  mainImageFile: File | null = null;
  additionalImageFiles: File[] = [];
  mainImagePreviewUrl: string | null = null;
  additionalImagePreviewUrls: string[] = [];
  pdfFile: File | null = null;
  pdfPreviewUrl!: any;
  isLoading = false;
  imageChangedEvent: any = '';
  croppedImage: string | null = null;
  showCropper = false;

  constructor(
    private adminService: AdminService,
    private activatedRoute: ActivatedRoute
  ) {
  }

  get pdfFileName(): string {
    if (!this.pdfPreviewUrl) return '';
    const fullFileName = this.pdfPreviewUrl.split('/').pop();
    return fullFileName?.split('_').pop() || '';
  }

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe(params => {
      this.newsId = params.get('id') || '';
      if (this.newsId) {
        this.loadNewsData();
      }
    });
  }

  loadNewsData(): void {
    this.adminService.getNewsById(this.newsId).subscribe((news: any) => {
      this.mainImagePreviewUrl = news.mainImageUrl;
      this.pdfPreviewUrl = news.pdfUrl;
      this.additionalImagePreviewUrls = news.additionalImageUrls || [];

      this.mainImageFile = null;
      this.pdfFile = null;
      this.additionalImageFiles = [];
    });
  }

  onMainImageChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.mainImageFile = input.files[0];
      this.mainImagePreviewUrl = URL.createObjectURL(this.mainImageFile);

      this.imageChangedEvent = event;
      this.showCropper = true;
    }
  }

  imageCropped(event: ImageCroppedEvent) {
    if (event.blob) {
      this.convertBlobToBase64(event.blob).then((base64) => {
        this.croppedImage = base64;
      });
    }
  }

  convertBlobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  }

  imageLoaded() {
  }

  cropperReady() {
  }

  loadImageFailed() {
    Swal.fire({
      title: 'Error',
      text: 'Failed to load the image.',
      icon: 'error',
      confirmButtonText: 'OK',
    });
  }

  saveCroppedImage() {
    if (!this.croppedImage) {
      console.error("No cropped image available to save.");
      return;
    }

    this.mainImagePreviewUrl = this.croppedImage;
    this.showCropper = false;

    this.mainImageFile = this.dataURLtoFile(this.croppedImage, "cropped-image.png");
  }

  dataURLtoFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  onAdditionalImagesChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.additionalImageFiles = Array.from(input.files);
      this.additionalImagePreviewUrls = this.additionalImageFiles.map((file) =>
        URL.createObjectURL(file)
      );
    }
  }

  onPdfChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const maxSize = 5 * 1024 * 1024; // 5 MB

      if (file.type !== 'application/pdf') {
        Swal.fire({
          title: 'Error',
          text: 'Only PDF files are allowed.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }

      if (file.size > maxSize) {
        Swal.fire({
          title: 'Error',
          text: 'The PDF file exceeds the maximum size of 5 MB.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        return;
      }

      this.pdfFile = file;
      this.pdfPreviewUrl = URL.createObjectURL(file);
    }
  }

  onSubmit(): void {
    if (!this.mainImageFile && this.additionalImageFiles.length === 0 && !this.pdfFile) {
      Swal.fire({
        title: 'Error',
        text: 'Please upload at least one file.',
        icon: 'error',
        confirmButtonText: 'OK',
      });
      return;
    }

    this.isLoading = true;

    const uploadTasks: Promise<any>[] = [];

    if (this.mainImageFile) {
      uploadTasks.push(
        this.adminService.postMainImage(this.newsId, this.mainImageFile).toPromise()
      );
    }

    if (this.additionalImageFiles.length > 0) {
      uploadTasks.push(
        this.adminService.postAdditionalImages(this.newsId, this.additionalImageFiles).toPromise()
      );
    }

    if (this.pdfFile) {
      uploadTasks.push(
        this.adminService.postPdfImage(this.newsId, this.pdfFile).toPromise()
      );
    }

    Promise.all(uploadTasks)
      .then(() => {
        this.isLoading = false;
        Swal.fire({
          title: 'Success',
          text: 'Images and PDF have been saved successfully!',
          icon: 'success',
          confirmButtonText: 'OK',
        });
      })
      .catch((error) => {
        this.isLoading = false;
        Swal.fire({
          title: 'Upload Failed',
          text: 'An error occurred while uploading files. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        console.error('Upload error:', error);
      });
  }

}
